/**
 * 社長設定パネル（権限管理画面の中で使う）
 *
 * 【何を解決するか】
 * モバイルアプリの「GB組織」タブを社長に出すには、次の3つがすべて揃う必要がある。
 *   ① DB の users.permissionLevel = COMPANY_WIDE
 *   ② Cognito の custom:permissionLevel = COMPANY_WIDE
 *   ③ 会社の hasSoshiki = true
 * ところが従来は ①=権限管理 / ②=データ同期 / ③=会社管理 と3画面に分かれており、
 * ②を忘れると「DBは社長なのにアプリでは社員のまま」という状態になっていた
 * （モバイルは Cognito のクレームだけを見るため）。
 * このパネルは3つの状態を1画面で見せ、1操作でまとめて設定する。
 *
 * 【注意】権限変更APIはDBのみを更新する。Cognitoへの反映は data-sync の
 * 個別同期(syncSingleUser)が唯一の経路なので、必ず続けて実行する。
 */
import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { permissionApi } from '../api/permissionApi';
import { organizationApi } from '../api/organizationApi';
import { dataSyncApi } from '../api/dataSyncApi';
import { PermissionLevel } from '../types/permission.types';
import type { Company } from '../types/organization.types';

export interface ExecutiveTarget {
  id: string;
  name: string;
  email: string;
  companyId?: string;
  companyName?: string;
  permissionLevel: PermissionLevel;
}

interface Props {
  target: ExecutiveTarget;
  /** 設定完了後に一覧を再取得させる */
  onDone?: () => void;
}

type Step = { label: string; ok: boolean; detail?: string };

export const ExecutiveSetupPanel: React.FC<Props> = ({ target, onDone }) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Step[] | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alsoEnableSoshiki, setAlsoEnableSoshiki] = useState(true);

  const isCompanyWide = target.permissionLevel === PermissionLevel.COMPANY_WIDE;
  const hasSoshiki = company?.hasSoshiki ?? undefined;

  /** 会社のエンタイトルメント（hasSoshiki）を確認する。 */
  const checkCompany = useCallback(async () => {
    if (!target.companyId) {
      setError('この社員には会社が紐づいていません。先に会社を設定してください。');
      return;
    }
    try {
      setChecking(true);
      setError(null);
      const list = await organizationApi.getCompanies();
      const c = list.find((x) => x.id === target.companyId) ?? null;
      setCompany(c);
      if (!c) setError('会社情報が取得できませんでした。');
    } catch (e) {
      setError(e instanceof Error ? e.message : '会社情報の取得に失敗しました');
    } finally {
      setChecking(false);
    }
  }, [target.companyId]);

  /** ①DB権限 → ②Cognito同期 → ③会社設定 をまとめて実行する。 */
  const runSetup = useCallback(async () => {
    setConfirmOpen(false);
    const steps: Step[] = [];
    try {
      setRunning(true);
      setError(null);
      setResult(null);

      // ① DB の権限を COMPANY_WIDE に
      if (isCompanyWide) {
        steps.push({ label: 'DBの権限（COMPANY_WIDE）', ok: true, detail: '既に設定済み' });
      } else {
        await permissionApi.setPermissionLevel(target.id, {
          permissionLevel: PermissionLevel.COMPANY_WIDE,
        });
        steps.push({ label: 'DBの権限（COMPANY_WIDE）', ok: true, detail: '設定しました' });
      }

      // ② Cognito へ反映（ここを忘れるとアプリ側は社員のまま）
      const sync = await dataSyncApi.syncSingleUser(target.id);
      steps.push({
        label: 'Cognitoへの反映',
        ok: Boolean(sync?.success ?? true),
        detail: sync?.action ? `処理: ${sync.action}` : '同期しました',
      });

      // ③ 会社の hasSoshiki
      if (alsoEnableSoshiki && target.companyId && company && company.hasSoshiki !== true) {
        await organizationApi.updateCompany(target.companyId, { hasSoshiki: true });
        steps.push({ label: '会社のGB組織 利用可否', ok: true, detail: '有効にしました' });
        setCompany({ ...company, hasSoshiki: true });
      } else {
        steps.push({
          label: '会社のGB組織 利用可否',
          ok: company?.hasSoshiki === true,
          detail:
            company?.hasSoshiki === true
              ? '既に有効'
              : '無効のままです（GB組織タブは表示されません）',
        });
      }

      setResult(steps);
      onDone?.();
    } catch (e) {
      setResult(steps);
      setError(e instanceof Error ? e.message : '設定に失敗しました');
    } finally {
      setRunning(false);
    }
  }, [isCompanyWide, target, company, alsoEnableSoshiki, onDone]);

  const statusChip = (ok: boolean | undefined, okText: string, ngText: string) => {
    if (ok === undefined) return <Chip size="small" label="未確認" />;
    return <Chip size="small" color={ok ? 'success' : 'warning'} label={ok ? okText : ngText} />;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        社長向け画面の有効化
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        モバイルアプリで「GB組織」タブを表示するには、次の3つがすべて必要です。
      </Typography>

      <Stack spacing={1} sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ minWidth: 200 }}>
            ① DBの権限（COMPANY_WIDE）
          </Typography>
          {statusChip(isCompanyWide, '設定済み', '未設定')}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ minWidth: 200 }}>
            ② Cognitoへの反映
          </Typography>
          <Chip size="small" label="実行して反映" />
          <Typography variant="caption" color="text.secondary">
            ※ アプリはCognitoの値だけを見ます
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ minWidth: 200 }}>
            ③ 会社のGB組織 利用可否
          </Typography>
          {statusChip(hasSoshiki, '有効', '無効')}
          <Button size="small" onClick={checkCompany} disabled={checking}>
            {checking ? '確認中…' : '確認する'}
          </Button>
          {company && (
            <Typography variant="caption" color="text.secondary">
              {company.name}
            </Typography>
          )}
        </Box>
      </Stack>

      {hasSoshiki === false && (
        <FormControlLabel
          control={
            <Checkbox
              checked={alsoEnableSoshiki}
              onChange={(e) => setAlsoEnableSoshiki(e.target.checked)}
            />
          }
          label="あわせて会社の「GB組織」を有効にする"
        />
      )}

      <Box mt={1}>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={running || !target.companyId}
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {running ? '設定中…' : '社長として設定する'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert severity={result.every((s) => s.ok) ? 'success' : 'warning'} sx={{ mt: 2 }}>
          {result.map((s) => (
            <div key={s.label}>
              {s.ok ? '✓' : '!'} {s.label}
              {s.detail ? ` — ${s.detail}` : ''}
            </div>
          ))}
          <Box mt={1}>
            <strong>対象者に再ログインしてもらってください。</strong>
            アプリが持っているトークンには古い権限が入っているため、
            再ログインするまでタブは切り替わりません。
          </Box>
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>社長として設定します</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Box mb={1}>
              <strong>
                {target.name}（{target.email}）
              </strong>
              {target.companyName ? ` / ${target.companyName}` : ''}
            </Box>
            この操作で次を行います。
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li>DBの権限を COMPANY_WIDE にする</li>
              <li>Cognitoへ反映する</li>
              {hasSoshiki === false && alsoEnableSoshiki && (
                <li>会社の「GB組織」を有効にする（会社全体に影響します）</li>
              )}
            </ul>
            <Box mt={1.5} color="warning.main">
              COMPANY_WIDE は全社員のデータを閲覧できる権限です。対象者をご確認ください。
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>キャンセル</Button>
          <Button variant="contained" color="primary" onClick={runSetup}>
            設定する
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExecutiveSetupPanel;
